<template>
    <TextFormField 
        :label="label"
        :errors="errors"
        :icon="icon"
        :name="name"
        :modelValue="modelValue && modelValue.number ? modelValue.number : ''"
        @update:modelValue="onNumberUpdate"
    >
        <template v-slot:before>
            <div class="min-w-[80px]">
                <div 
                    class="tel-select-container"
                    @click.stop="toggleDropdown"
                    @keydown.esc="isOpen = false"
                    @keydown.down.prevent="handleArrowDown"
                    @keydown.up.prevent="handleArrowUp"
                    @keydown.enter.prevent="selectHighlighted"
                >
                    <!-- Selected Value Display -->
                    <div class="tel-select-display" tabindex="0" ref="selectDisplay">
                        <div v-if="selectedCountry" class="flex justify-between items-center w-full">
                            <div class="flex items-center">
                                <img
                                    v-if="selectedCountry.countryCode"
                                    :src="getFlagUrl(selectedCountry.countryCode)"
                                    class="mr-2 w-6 h-4"
                                    :alt="selectedCountry.countryCode"
                                />
                                <span class="mr-2 text-[14px] font-medium leading-none">+{{ selectedCountry.countryCallingCode }}</span>
                            </div>
                            <span class="inline-block mr-2 ml-auto w-2 h-2 border-r-2 border-b-2 border-gray-500 rotate-45"></span>
                        </div>
                        <div v-else class="flex justify-between items-center w-full">
                            <span>{{ in18 && in18['attribute_shipping_country_option'] || 'Select country' }}</span>
                            <span class="inline-block mr-2 ml-auto w-2 h-2 border-r-2 border-b-2 border-gray-500 rotate-45"></span>
                        </div>
                    </div>

                    <!-- Dropdown Menu -->
                    <div v-if="isOpen" class="tel-select-dropdown" ref="dropdown">
                        <!-- Search Input -->
                        <div class="tel-select-search-container">
                            <input
                                ref="searchInput"
                                type="text"
                                v-model="searchQuery"
                                @input="onSearch"
                                @click.stop
                                @keydown.stop
                                :placeholder="in18 && in18['search'] || 'Search'"
                                class="tel-select-search"
                            />
                        </div>

                        <!-- Options List -->
                        <div class="tel-select-options-container">
                            <div 
                                v-for="(option, index) in filteredOptions" 
                                :key="option.countryCode ? `${option.countryCode}-${option.countryCallingCode}` : `index-${index}`"
                                class="tel-select-option" 
                                :class="{
                                    'tel-select-option-highlighted': highlightedIndex === index,
                                    'tel-select-option-selected': selectedCountry && selectedCountry.countryCallingCode === option.countryCallingCode
                                }"
                                @click.stop="selectOption(option)"
                                @mouseover="highlightedIndex = index"
                            >
                                <div class="flex items-center">
                                    <img
                                        :src="getFlagUrl(option.countryCode)"
                                        class="mr-2 w-7 h-5"
                                        :alt="option.countryCode"
                                    />
                                    <span>+{{ option.countryCallingCode }}</span>
                                </div>
                            </div>
                            <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-gray-600">
                                {{ in18 && in18['search_no_results_found'] || 'No results found' }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </TextFormField>
</template>

<script>
import TextFormField from "./TextFormField.vue";
import { all } from "country-codes-list";

export default {
    name: "TelFormField",
    components: {
        TextFormField,
    },
    props: {
        label: {
            type: String,
            required: true,
        },
        errors: {
            type: [Array, null, undefined],
            required: false,
        },
        icon: {
            type: String,
            required: false,
            default: "form-phone",
        },
        modelValue: {
            required: true,
            type: Object,
            default: () => ({
                number: null,
                countryCallingCode: null,
            }),
        },
        name: {
            type: String,
            required: true,
        },
        in18: {
            type: Object,
            required: false,
        }
    },
    
    emits: ["update:modelValue"],
    data() {
        const allCountries = all();
        let countriesArr = Array.isArray(allCountries)
            ? allCountries
            : Object.values(allCountries);
        
        return {
            countries: countriesArr,
            isOpen: false,
            searchQuery: "",
            highlightedIndex: 0,
            clickOutsideHandler: null
        };
    },
    computed: {
        selectedCountry() {
            if (!this.modelValue || typeof this.modelValue !== "object")
                return null;
            const found = this.countries.find(
                (c) => c.countryCallingCode == this.modelValue.countryCallingCode
            );
            return found;
        },
        filteredOptions() {
            let result;
            
            if (!this.searchQuery) {
                result = [...this.countries];
            } else {
                const query = this.searchQuery.toLowerCase();
                result = this.countries.filter(country => {
                    const code = country.countryCode?.toLowerCase() || '';
                    const callingCode = country.countryCallingCode?.toLowerCase() || '';
                    const name = country.countryNameLocal?.toLowerCase() || '';
                    
                    return code.includes(query) || 
                           callingCode.includes(query) || 
                           name.includes(query);
                });
            }
            
            // Deduplicate by country code + calling code combination
            const uniqueMap = {};
            return result.filter(country => {
                const key = `${country.countryCode || ''}-${country.countryCallingCode || ''}`;
                if (uniqueMap[key]) {
                    return false;
                }
                uniqueMap[key] = true;
                return true;
            });
        }
    },
    mounted() {
        // Set up click outside handler
        this.clickOutsideHandler = (event) => {
            if (this.isOpen && this.$refs.dropdown && !this.$el.contains(event.target)) {
                this.isOpen = false;
            }
        };
        
        document.addEventListener('mousedown', this.clickOutsideHandler);
    },
    beforeUnmount() {
        // Clean up event listener
        if (this.clickOutsideHandler) {
            document.removeEventListener('mousedown', this.clickOutsideHandler);
        }
    },
    methods: {
        getFlagUrl(countryCode) {
            if (!countryCode) return "";
            return `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;
        },
        onNumberUpdate(number) {
            if (!this.modelValue || typeof this.modelValue !== "object") {
                this.$emit("update:modelValue", { number, countryCallingCode: null });
            } else {
                this.$emit("update:modelValue", { ...this.modelValue, number });
            }
        },
        toggleDropdown() {
            this.isOpen = !this.isOpen;
            
            if (this.isOpen) {
                this.$nextTick(() => {
                    // Focus the search input when dropdown is opened
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                    
                    // Set highlighted index to selected country if present
                    if (this.selectedCountry) {
                        const index = this.filteredOptions.findIndex(
                            option => option.countryCallingCode === this.selectedCountry.countryCallingCode
                        );
                        if (index !== -1) {
                            this.highlightedIndex = index;
                        }
                    }
                });
            }
        },
        selectOption(country) {
            if (!country) return;

            if (!this.modelValue || typeof this.modelValue !== "object") {
                this.$emit("update:modelValue", {
                    number: null,
                    countryCallingCode: country.countryCallingCode
                });
            } else {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    countryCallingCode: country.countryCallingCode
                });
            }
            
            // Close the dropdown
            this.isOpen = false;
            
            // Reset search query
            this.searchQuery = "";
            
            // Focus on display element
            this.$nextTick(() => {
                if (this.$refs.selectDisplay) {
                    this.$refs.selectDisplay.focus();
                }
            });
        },
        onSearch() {
            // Reset highlighted index when search changes
            this.highlightedIndex = 0;
        },
        handleArrowDown() {
            if (!this.isOpen) {
                this.toggleDropdown();
                return;
            }
            
            if (this.filteredOptions.length > 0) {
                this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredOptions.length;
                this.scrollToHighlighted();
            }
        },
        handleArrowUp() {
            if (!this.isOpen) {
                this.toggleDropdown();
                return;
            }
            
            if (this.filteredOptions.length > 0) {
                this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
                this.scrollToHighlighted();
            }
        },
        scrollToHighlighted() {
            this.$nextTick(() => {
                const container = this.$refs.dropdown;
                const highlighted = container?.querySelector('.tel-select-option-highlighted');
                
                if (container && highlighted) {
                    // Check if highlighted item is not visible
                    const containerRect = container.getBoundingClientRect();
                    const highlightedRect = highlighted.getBoundingClientRect();
                    
                    if (highlightedRect.bottom > containerRect.bottom) {
                        highlighted.scrollIntoView(false); // Align to bottom
                    } else if (highlightedRect.top < containerRect.top) {
                        highlighted.scrollIntoView(true); // Align to top
                    }
                }
            });
        },
        selectHighlighted() {
            if (this.isOpen && this.filteredOptions.length > 0) {
                this.selectOption(this.filteredOptions[this.highlightedIndex]);
            } else {
                this.toggleDropdown();
            }
        }
    }
};
</script>

<style>
/* Custom Tel Select Component */
.tel-select-container {
    position: relative;
    width: 100%;
    min-width: 80px;
    font-size: 1rem;
    line-height: 1.5;
    user-select: none;
}

.tel-select-display {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* min-height: 42px; */
    outline: none;
}

.tel-select-display:focus, .tel-select-display:active {
    outline: none;
}

.tel-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    min-width: 150px;
    max-height: 250px;
    overflow-y: auto;
    background-color: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    z-index: 50;
    margin-top: 4px;
}

.tel-select-search-container {
    padding: 8px;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
}

.tel-select-search {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    outline: none;
    font-size: 0.875rem;
}

.tel-select-search:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
}

.tel-select-options-container {
    max-height: 180px;
    overflow-y: auto;
}

.tel-select-option {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.1s ease;
}

.tel-select-option:hover {
    background-color: #f3f4f6;
}

.tel-select-option-highlighted {
    background-color: #e5e7eb;
}

.tel-select-option-selected {
    background-color: #eef2ff;
    color: #4f46e5;
    font-weight: 500;
}

/* Safari-specific fixes */
@media not all and (min-resolution:.001dpcm) { 
    @supports (-webkit-appearance:none) {
        .tel-select-dropdown {
            /* Ensure the dropdown stays put in Safari */
            transform: translateZ(0);
        }
        
        .tel-select-display:focus {
            /* Enhance focus visibility in Safari */
            outline: none;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4);
        }
    }
}
</style>
